import { Route } from 'vue-router';
import { RawLocation } from 'vue-router';
import { store } from '@/store';
import { i18n } from '@/i18n';
import { NOTIFICATION_PATH } from '@/constants/uri';
import { redirectToUserPage } from './authentication';
import { Next } from './types';
import Vue from 'vue';

const LOGOUT_NOTIFICATION = 'success.logout.ok';

export function handleNotificationFromQuery(notification?: string): void {
	if (!notification) {
		return;
	}

	const [type, ...messageParts] = notification.split('.');
	const messageLabel = '.' + messageParts.join('.');
	const translationKey = `notification${messageLabel}`;

	if (i18n.te(translationKey)) {
		const message = i18n.t(translationKey);
		store.dispatch(NOTIFICATION_PATH, { type, message });
	}
}

/**
 * Gère le mode maintenance
 * @returns La route de redirection ou null si pas de redirection nécessaire
 */
export function handleMaintenanceMode(
	to: Route,
	isMaintenanceActive: boolean
): RawLocation | null {
	const isMaintenancePage = to.name === 'maintenance';

	if (isMaintenanceActive && !isMaintenancePage) {
		return { name: 'maintenance' };
	}

	if (!isMaintenanceActive && isMaintenancePage) {
		return { name: 'home' };
	}

	return null;
}

/**
 * Gère l'authentification et les redirections
 */
export async function handleAuthentication(to: Route, next: Next): Promise<void> {
	const hasUserInfo = store.getters['auth/hasUserInfo'];
	const requiresAuth = to.meta?.logged === true;
	const isLoginPage = to.name === 'login';

	// Utilisateur non authentifié essayant d'accéder à une page protégée
	if (!hasUserInfo && requiresAuth) {
		await handleUnauthenticatedAccess(to, next);
		return;
	}

	// Gestion spécifique de la page de login
	if (isLoginPage) {
		await handleLoginPageAccess(to, hasUserInfo, next);
		return;
	}

	next();
}

/**
 * Gère l'accès non authentifié à une page protégée
 */
async function handleUnauthenticatedAccess(to: Route, next: Next): Promise<void> {
	const isSspa = Vue.prototype.$SSPA_ENABLED;

	try {
		await store.dispatch('auth/getMyUser');

		if (isSspa) {
			next();
			return;
		}

		if (to.name === 'login') {
			await redirectToUserPage(next);
		} else {
			next();
		}
	} catch {
		if (isSspa) {
			await store.dispatch(NOTIFICATION_PATH, {
				type: 'error',
				message: 'Erreur de chargement de l\'utilisateur MCE'
			});
			next();
			return;
		}

		if (to.name === 'login') {
			await redirectToUserPage(next);
		} else {
			next({
				name: 'login',
				params: { userType: 'assure' }
			});
		}
	}
}

/**
 * Gère l'accès à la page de login
 */
async function handleLoginPageAccess(to: Route, hasUserInfo: boolean, next: Next): Promise<void> {
	if (!hasUserInfo) {
		if (to.query.notification === LOGOUT_NOTIFICATION) {
			next();
			return;
		}

		try {
			await store.dispatch('auth/getMyUser');
			await redirectToUserPage(next);
		} catch {
			next();
		}
	} else {
		await redirectToUserPage(next);
	}
}
